/**
 * MMEC Structured Intelligence Agent — preset questions (AR/EN).
 * Clicking a preset pastes the question and uses report-only mode for consistent, credit-efficient answers.
 */

export interface PresetQuestion {
  id: string;
  ar: string;
  en: string;
  /** For stock-specific presets: placeholder to replace with symbol, e.g. {symbol} */
  symbolPlaceholder?: boolean;
  /** Instant reply: show saved alert profile (no AI call). */
  showAlertSettings?: boolean;
}

export const MMEC_PRESET_MARKET_VIEW: PresetQuestion[] = [
  {
    id: '1',
    ar: 'ما هو نظام السوق الحالي؟',
    en: 'What is the current market regime?',
  },
  {
    id: '2',
    ar: 'هل البيئة مناسبة لبيع الـ Premium؟',
    en: 'Is the environment suitable for selling premium?',
  },
  {
    id: '3',
    ar: 'أين تتركز جدران الجاما الرئيسية اليوم؟',
    en: 'Where are the main gamma walls concentrated today?',
  },
  {
    id: '4',
    ar: 'ما أفضل 3 فرص عالية الثقة اليوم؟',
    en: 'What are the top 3 high-conviction opportunities today?',
  },
  {
    id: '5',
    ar: 'ما المخاطر الرئيسية الحالية؟',
    en: 'What are the main risks currently?',
  },
];

export const MMEC_PRESET_STOCK_ANALYSIS: PresetQuestion[] = [
  {
    id: '6',
    ar: 'ما الاتجاه المؤسسي الحالي لهذا السهم؟',
    en: 'What is the current institutional direction for this stock?',
    symbolPlaceholder: true,
  },
  {
    id: '7',
    ar: 'أين أقوى مناطق الدعم والمقاومة (Gamma Walls)؟',
    en: 'Where are the strongest support and resistance levels (Gamma Walls)?',
    symbolPlaceholder: true,
  },
  {
    id: '8',
    ar: 'هل السهم مناسب لبيع Put Spread؟',
    en: 'Is this stock suitable for selling put spread?',
    symbolPlaceholder: true,
  },
  {
    id: '9',
    ar: 'هل السهم مناسب لبيع Call Spread؟',
    en: 'Is this stock suitable for selling call spread?',
    symbolPlaceholder: true,
  },
  {
    id: '10',
    ar: 'ما هو وضع التقلب (Volatility Profile)؟',
    en: 'What is the volatility profile?',
    symbolPlaceholder: true,
  },
  {
    id: '11',
    ar: 'ما إطار التنفيذ المقترح؟',
    en: 'What is the suggested execution framework?',
    symbolPlaceholder: true,
  },
  {
    id: '12',
    ar: 'ما الذي يبطل هذا السيناريو؟',
    en: 'What would invalidate this scenario?',
    symbolPlaceholder: true,
  },
];

/** Presets for Option Helper / Strategy mode: start strategy discovery (AI will ask questions then recommend). */
/** Flow / liquidity alerts — AR/EN presets (AI configures alert system). */
export const MMEC_PRESET_ALERTS: PresetQuestion[] = [
  {
    id: 'a-show',
    ar: 'اعرض إعدادات التنبيه الحالية',
    en: 'Show my current alert settings',
    showAlertSettings: true,
  },
  {
    id: 'a1',
    ar: 'فعّل إشعارات المتصفح للتدفق الكبير على SPX',
    en: 'Enable browser notifications for big SPX flow',
  },
  {
    id: 'a2',
    ar: 'نبّهني على SPX عند علاوة فوق 500 ألف دولار على الشراء',
    en: 'Alert me on SPX when premium over $500k on buys',
    symbolPlaceholder: true,
  },
  {
    id: 'a3',
    ar: 'نبّهني على SPX بعد جلب Stable عند تدفق كبير',
    en: 'Alert on SPX after stable fetch for whale flow',
    symbolPlaceholder: true,
  },
  {
    id: 'a4',
    ar: 'تنبيه عند فتح اهتمام (OI) فوق 10 آلاف وحجم فوق 500 عقد',
    en: 'Alert when OI over 10k and volume over 500 contracts',
  },
  {
    id: 'a5',
    ar: 'نبّهني على خيارات الشراء فقط عند علاوة فوق مليون',
    en: 'Notify on calls only when premium over $1M',
  },
  {
    id: 'a6',
    ar: 'نبّهني على خيارات البيع فقط عند بيع (sell flow)',
    en: 'Notify on puts only for sell-side flow',
  },
  {
    id: 'a7',
    ar: 'أوقف كل التنبيهات',
    en: 'Turn off all alerts',
  },
  {
    id: 'a8',
    ar: 'امسح كل قواعد التنبيه',
    en: 'Clear all alert rules',
  },
  {
    id: 'a9',
    ar: 'أضف معامل مخصص: علاوة مقسوم على OI — نبّهني عندما يكون أكبر من 2',
    en: 'Add custom field premium divided by OI — alert when greater than 2',
  },
  {
    id: 'a10',
    ar: 'معاينة شريط QuestDB لـ SPX علاوة فوق 250 ألف',
    en: 'Preview QuestDB tape for SPX premium over $250k',
    symbolPlaceholder: true,
  },
];

export const MMEC_PRESET_STRATEGY: PresetQuestion[] = [
  {
    id: 's1',
    ar: 'ساعدني أختار استراتيجية خيارات مناسبة بناءً على البيانات والمشاعر',
    en: 'Help me pick an options strategy based on the data and sentiment',
  },
  {
    id: 's2',
    ar: 'اقترح استراتيجية لـ هذا السهم',
    en: 'Suggest a strategy for this stock',
    symbolPlaceholder: true,
  },
  {
    id: 's3',
    ar: 'أريد بيع علاوة — أي استراتيجية تناسب؟',
    en: 'I want to sell premium — which strategy fits?',
  },
  {
    id: 's4',
    ar: 'أنا متجه مع السوق — ماذا تشتري؟',
    en: 'I am directional with the market — what should I trade?',
  },
];

export function getPresetQuestionText(
  preset: PresetQuestion,
  language: 'ar' | 'en',
  symbol?: string
): string {
  let text = language === 'ar' ? preset.ar : preset.en;
  const sym = symbol?.trim();
  if (preset.symbolPlaceholder && sym) {
    text = text
      .replace(/هذا السهم/g, sym)
      .replace(/this stock/gi, sym)
      .replace(/SPX/g, sym)
      .replace(/spx/gi, sym);
  }
  return text;
}
